// Port of mobile src/services/accountLink.ts. Same link-or-merge flow:
// try to LINK the anonymous account to the provider; if the provider account
// already backs another Firebase user, capture proof of ownership of the
// anonymous account FIRST, then sign into the existing account and move the
// anonymous account's notes into it server-side (mergeAccounts callable).
//
// UI concerns (confirm dialog, toasts) are injected by the caller so this
// stays a pure service like on mobile.
import {auth} from '../firebase';
import {linkWithProvider, signInWithProvider} from './authService';
import {mergeAccounts} from './functionsService';

export type LinkProvider = 'apple' | 'google';

export interface LinkResult {
  status: 'linked' | 'merged' | 'cancelled' | 'error';
  mergedNotes?: number;
}

const isCancelled = (error: unknown): boolean => {
  const code = String((error as {code?: string})?.code ?? '');
  return (
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request' ||
    code === 'auth/user-cancelled'
  );
};

export const linkOrMerge = async (
  provider: LinkProvider,
  confirmMerge: () => Promise<boolean>,
): Promise<LinkResult> => {
  try {
    await linkWithProvider(provider);
    return {status: 'linked'};
  } catch (error) {
    if (isCancelled(error)) {
      return {status: 'cancelled'};
    }
    if ((error as {code?: string})?.code !== 'auth/credential-already-in-use') {
      console.warn(error);
      return {status: 'error'};
    }
  }

  // Provider account already exists elsewhere → offer merge.
  const anonUser = auth.currentUser;
  if (!anonUser) {
    return {status: 'error'};
  }
  // Proof of ownership of the anonymous account, captured BEFORE switching.
  const sourceIdToken = await anonUser.getIdToken(true);

  if (!(await confirmMerge())) {
    return {status: 'cancelled'};
  }

  try {
    // Fresh provider prompt — the pending credential from the link error is
    // not reliably reusable across the popup flows.
    await signInWithProvider(provider);
    const result = await mergeAccounts(sourceIdToken);
    return {status: 'merged', mergedNotes: result.mergedNotes};
  } catch (error) {
    if (isCancelled(error)) {
      return {status: 'cancelled'};
    }
    console.warn(error);
    return {status: 'error'};
  }
};
